import classnames from 'classnames/bind'
import React from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import {
  getProductTextFromProductState,
  getProductNoteFromProductState,
} from '~/lib/get-product-infos-from-product-state'

import InlineCta from '~/components/UI/InlineCta'
import { ProductHeaderProps } from '~/components/UI/ProductHeader'
import RichText from '~/components/UI/RichText'

import { useStyle } from '~/providers/StyleProvider'

import { useINPTrick } from '~/hooks/useINPTrick'

import { CTA } from '~/data/dictionary'
import { ProductProps } from '~/data/global-data'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type ProductDetailsBottomInformationsProps = {
  className?: string
  hasSizeGuide?: boolean
  hasSizeVariant?: boolean
  isOutOfStock?: boolean
  isUniqueVariant?: boolean
  isThreeSizesVariants?: boolean
  isVariantAdjusting?: boolean
  isVariantSellable?: boolean
  onClickSizeGuide?: (e) => void
  isAdjusting?: ProductHeaderProps['isAdjusting']
  isStockAlert?: ProductHeaderProps['isStockAlert']
  isSpecialOrder?: ProductHeaderProps['isSpecialOrder']
  adjustableOffset?: ProductHeaderProps['adjustableOffset']
  preOrder?: ProductHeaderProps['preOrder']
  notes?: ProductProps['notes']
  uniqueVariantSize?: string
}

function ProductDetailsBottomInformations({
  className,
  isAdjusting,
  hasSizeGuide,
  hasSizeVariant,
  isSpecialOrder,
  isStockAlert,
  isOutOfStock,
  isUniqueVariant,
  isThreeSizesVariants,
  isVariantAdjusting,
  isVariantSellable,
  onClickSizeGuide,
  preOrder,
  notes,
  uniqueVariantSize,
}: ProductDetailsBottomInformationsProps) {
  const t = useTranslate()
  const { hasInteracted, onInteract } = useINPTrick()

  const textInfosStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskRegular,
  })

  const noteLabelStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskMedium,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const noteTextStyle = useStyle({
    textPreset: GlobalTextPreset.Label12SangBleu,
    textStyling: GlobalTextStyling.Italic,
  })

  const currentNote =
    notes?.[
      getProductNoteFromProductState({
        isVariantAdjusting,
        isVariantSellable,
        isStockAlert,
        isOutOfStock,
        isSpecialOrder,
        isPreOrder: preOrder?.isActive,
      })
    ] ?? '' // Keep something empty to avoid shifting

  return (
    <div className={cx(css.ProductDetailsBottomInformations, className)}>
      <div className={css.top}>
        <span className={cx(css.textInfos, textInfosStyle)}>
          {isUniqueVariant
            ? uniqueVariantSize && (
                <div className={cx(css.size, { isOutOfStock })}>
                  {uniqueVariantSize}
                </div>
              )
            : t(
                getProductTextFromProductState({
                  isAdjusting,
                  isThreeSizesVariants,
                  isVariantAdjusting,
                  isVariantSellable,
                  isOutOfStock,
                  isSpecialOrder,
                }),
              )}
        </span>

        {hasSizeGuide && hasSizeVariant && (
          <div className={css.toggleSizeGuide}>
            <InlineCta
              className={cx(
                css.sizeGuideButton,
                hasInteracted && css.hasInteracted,
              )}
              textPreset={GlobalTextPreset.Cta10GroteskRegular}
              onClick={(e) => onInteract(() => onClickSizeGuide(e))}>
              {t(CTA.SIZE_GUIDE)}
            </InlineCta>
          </div>
        )}
      </div>

      {currentNote && (
        <div className={css.bottom}>
          <div className={css.note}>
            <span className={cx(css.noteTitle, noteLabelStyle)}>
              Note&nbsp;:
            </span>
            <RichText
              replacePatterns={
                preOrder?.isActive
                  ? [
                      {
                        stringToReplace: '%{delivery_date}',
                        replacementString: preOrder?.deliveryDate ?? '',
                      },
                    ]
                  : null
              }
              render={currentNote}
              className={cx(css.noteText, noteTextStyle)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

ProductDetailsBottomInformations.defaultProps = {}

export default ProductDetailsBottomInformations
